import LoaderV2 from "core/loader/LoaderV2";
import Button from "core/buttons/button/Button";
import InfoContext from "pages/InfoContext";
import InfoHeaderAction from "pages/info-header/InfoHeaderAction";
import MissionBillingInfo from "components/sections-mission/MissionBillingInfo";
import MissionGeneralInfo from "components/sections-mission/MissionGeneralInfo";
import _ from "lodash";
import { ENTITY } from "constants/info-header";
import { FIELD_REQUIRED } from "constants/popup-codes";
import { FormattedMessage } from "react-intl";
import { MISSION_DEFAULT_VALUES } from "constants/forms";
import {
  DOCUMENTS_BASE_URL_BY_ENTITY,
  FILTERS_FOR_NAMES,
  CLIENT_TYPES,
  POPUP_STATUS,
  SECTIONS,
  TAG_TYPES,
} from "constants/constants";
import { statusHandler } from "utils/helper";
import { useAppealContext } from "api/settings/appeal/AppealProvider";
import { useBranchesContext } from "api/settings/branches/BranchesProvider";
import { useClientContext } from "api/directory/client/ClientProvider";
import { useDybContext } from "api/external-apis/dyb/DybProvider";
import { useContext, useEffect, useMemo, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { useGeneralContext } from "api/directory/general/GeneralProvider";
import { useJobContext } from "api/settings/job/JobProvider";
import { useMissionContext } from "api/mission/MissionProvider";
import { usePersonContext } from "api/directory/person/PersonProvider";
import { useSettingsContext } from "api/settings/SettingsProvider";
import { switchDataNames } from "constants/MissionSwitchData";
import { useUserContext } from "api/user/UserProvider";
import { useCurrencyContext } from "api/settings/currency/CurrencyProvider";
import moment from "moment";
import {
  countWords,
  formatDisplayReceivedData,
  formatMissionBillsListToEdit,
  formatSubmittedData,
  getQueryParamsString,
  determineSectionForErrors,
  handleSwitchDataValidation,
  getBillingRequiredFields,
} from "utils/missionHelper";
import { useStaffContext } from "api/directory/staff/StaffProvider";
import { useMissionBillContext } from "api/mission/mission-bills/MissionBillsProvider";
import { useMissionFeeContext } from "api/mission/mission-fee/MissionFeeProvider";
import PastDateWarningModal from "components/modal/PastDateWarningModal";
import ValidationModal from "components/modal/ValidationModal";
import DocumentDownloadModal from "components/modal/DocumentDownloadModal";
import propTypes from "prop-types";
import { QUERY_STATUS } from "constants/constants";
import { useNavigate } from "react-router-dom";
import colors from "assets/styles/mixins/colors.scss";
import { useMissionManagerContext } from "api/mission/mission-manager/MissionManagerProvider";
import { useDocumentsContext } from "api/documents/DocumentsProvider";
import { useMissionBackupContext } from "api/mission/mission-backup/MissionBackupProvider";
import { useBranchContext } from "api/settings/branch/BranchProviders";

const DisplayMission = ({ setStatus, code, setCode }) => {
  const {
    readOnly,
    setReadOnly,
    setSlug,
    isMissionsPage = false,
  } = useContext(InfoContext);
  const {
    clearErrors,
    control,
    formState: { errors },
    handleSubmit,
    register,
    setErrors,
    setError,
    setValue,
    unregister,
    watch,
    getValues,
  } = useForm({
    defaultValues: MISSION_DEFAULT_VALUES,
  });
  const { getMissionIsBackupableById, missionIsBackupable } =
    useMissionBackupContext();
  const navigate = useNavigate();
  const { searchUsers, usersByIds } = useGeneralContext();
  const { appealList, appealListGetter } = useAppealContext();
  const { fetchBranches, branches } = useBranchesContext();
  const { getAssignedBranchWithoutScope } = useBranchContext();
  const {
    getClientById,
    clientById,
    getAssignedClients,
    assignedClients,
    getClientBillingAddressById,
    clientBillingAddressById,
    clientBillingAddressByIdStatus,
    setClientBillingAddressById,
    setClientBillingAddressByIdStatus,
  } = useClientContext();
  const { getDataTTV, dataTTV } = useSettingsContext();
  const { jobList, jobListGetter } = useJobContext();
  const {
    getPersonsByProfile,
    personsList,
    getPersonsByIds,
    personsByProfileList,
  } = usePersonContext();
  const { searchUsersByName, usersByName } = useDybContext();
  const { currencyList, currencyListGetter } = useCurrencyContext();
  const {
    lang,
    userScope,
    setApiErrorResponse,
    apiErrorResponse,
    userFilterStorage,
  } = useUserContext();
  const { getNamesByFilters, namesByFilters } = useGeneralContext();
  const {
    getMissionBillByMissionId,
    missionBillByMissionId,
    bulkEditMissionBill,
    createMissionBill,
    bulkEditMissionBillStatus,
  } = useMissionBillContext();
  const {
    missionBySlug,
    editMissionById,
    setMissionCreated,
    refresh,
    setRefresh,
    deleteMissionById,
    displayMission,
    editMissionStatus,
    setEditMissionStatus,
    deleteMissionStatus,
    setDeleteMissionStatus,
    getMissionsByFilters,
    missionsByFilters,
    getMissionBySlug,
    loadMissionStatus,
    setMissionById,
    setMissionBySlug,
    creationFileStatus,
    setCreationFileStatus,
    missionQuoteStatus,
    setMissionQuoteStatus,
  } = useMissionContext();

  const {
    id: missionId,
    access: missionAccess,
    manager_id: missionManagerId,
    worker_id: missionWorkerId,
    rate_coordinator: missionRateCoordinator,
    adv_id: missionAdvId,
    client_id: missionClientId,
    slug: missionSlug,
    signed_by_id: missionSignedById,
    currency_id: missionCurrencyId,
  } = missionBySlug || {};

  const canUpdateMission = missionAccess?.includes("update");
  const {
    getAvailableDocuments,
    availableDocuments,
    getDocument,
    getDocumentStatus,
    setGetDocumentStatus,
  } = useDocumentsContext();
  const [isEnabled, setIsEnabled] = useState(false);
  const [section, setSection] = useState(SECTIONS.GENERAL_INFO);
  const [changedActiveSection, setChangedActiveSection] = useState({
    section: SECTIONS.GENERAL_INFO,
    key: 0,
  });
  const [sectionChangeCounter, setSectionChangeCounter] = useState(0);
  const [currencyType, setCurrencyType] = useState("");
  const [managerBranches, setManagerBranches] = useState([6]);
  const downloadStatus = statusHandler({
    statusList: [getDocumentStatus, creationFileStatus, missionQuoteStatus],
  });

  const onDownloadModalClose = () => {
    setGetDocumentStatus(QUERY_STATUS.INITIAL);
    setCreationFileStatus(QUERY_STATUS.INITIAL);
    setMissionQuoteStatus(QUERY_STATUS.INITIAL);
  };

  const { staffByIds, getStaffByArrayId } = useStaffContext();
  const { getCurrentMissionFee, setCurrentMissionFee, missionFeeEditStatus } =
    useMissionFeeContext();
  const { missionManagerStatus } = useMissionManagerContext();
  const description = watch("description");
  const tableBills = watch("table_bills", missionBillByMissionId);
  const [missingCharValue, setMissingCharValue] = useState(0);
  const [errorNameMessage, setErrorNameMessage] = useState("");
  const [errorWordDescriptionMessage, setErrorWordDescriptionMessage] =
    useState("");
  const [descriptionMessageWordValue, setDescriptionMessageWordValue] =
    useState(countWords(description));
  const [cellHighlight, setCellHighlight] = useState(false);
  const [isValidated, setIsValidated] = useState(false);
  const [errorDashDescriptionMessage, setErrorDashDescriptionMessage] =
    useState("");
  const [descriptionMessageDashValue, setDescriptionMessageDashValue] =
    useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isLateEndDateWarningOpen, setIsLateEndDateWarningOpen] =
    useState(false);
  const [openModalIntent, setOpenModalIntent] = useState(null);
  const pendingSubmitDataRef = useRef(null);
  // Synchronous lock: blocks re-entrant submits before React re-renders the disabled button
  const isSubmittingRef = useRef(false);
  const currentDate = moment().format("YYYY-MM-DD");
  const currentYear = parseInt(moment().format("YYYY"));
  const BASE_URL = DOCUMENTS_BASE_URL_BY_ENTITY.MISSION;
  const [isFormInitialized, setIsFormInitialized] = useState(false);
  const currentMissionIdRef = useRef(null);
  const loadedUserIdsRef = useRef(new Set());
  const [localNamesById, setLocalNamesById] = useState({});
  const [selectedMissionBillsTableDate, setSelectedMissionBillsTableDate] =
    useState(currentDate);
  const [selectedMissionBillsTableYear, setSelectedMissionBillsTableYear] =
    useState(currentYear);

  const requiredUserIds = [
    missionManagerId,
    missionWorkerId,
    missionRateCoordinator,
    missionAdvId,
  ].filter(Boolean);

  const changeSection = (targetSection) => {
    setSection(targetSection);
    setSectionChangeCounter((prev) => prev + 1);
    setChangedActiveSection({
      section: targetSection,
      key: sectionChangeCounter + 1,
    });
  };

  useEffect(() => {
    setEditMissionStatus(QUERY_STATUS.INITIAL);
    setApiErrorResponse(null);
    return () => setApiErrorResponse(null);
  }, []);

  useEffect(() => {
    if (loadMissionStatus === QUERY_STATUS.NOT_FOUND) {
      navigate(`/${userScope}/not-found`, { replace: true });
    }
  }, [loadMissionStatus, userScope, navigate]);

  useEffect(() => {
    !readOnly && isValidated && errors && setCellHighlight(true);
  }, [errors, isValidated]);

  useEffect(() => {
    if (apiErrorResponse && !Array.isArray(apiErrorResponse)) {
      Object.entries(apiErrorResponse).forEach(([field, message]) => {
        setError(field, { type: "manual", message });
      });

      if (Object.keys(apiErrorResponse).length > 0) {
        setReadOnly(false);
      }

      const targetSection = determineSectionForErrors({
        errors: apiErrorResponse,
        watch,
        SECTIONS,
        dataTTV,
        isEdition: true,
      });
      if (targetSection) {
        changeSection(targetSection);
      }
    }
  }, [apiErrorResponse, setError]);

  useEffect(() => {
    section === SECTIONS.BILLING && getCurrentMissionFee(missionId);
  }, [section]);

  useEffect(() => {
    missionBySlug?.current_fee &&
      setValue("mission_fee", missionBySlug?.current_fee);
  }, [missionBySlug]);

  useEffect(() => {
    missionId && getMissionBillByMissionId(missionId);
    missionId && getMissionIsBackupableById({ id: missionId });
    if (requiredUserIds.length > 0) getPersonsByIds({ ids: requiredUserIds });
    if (missionClientId) {
      setCurrentMissionFee({});
      getCurrentMissionFee(missionId);
    }
    setRefresh(false);
  }, [missionBySlug, refresh, displayMission]);

  useEffect(() => {
    if (
      missionClientId &&
      localNamesById[missionClientId] &&
      !localNamesById[missionClientId].deleted
    ) {
      getClientById({
        id: missionClientId,
      });
    }
  }, [missionClientId, localNamesById]);

  const submitMission = async (data) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    try {
      const formattedMissionBills = formatMissionBillsListToEdit(
        tableBills?.filter((e) => !e.isNewBill)
      );
      const newFormattedMissionBills = formatMissionBillsListToEdit(
        tableBills?.filter((e) => e.isNewBill)
      );
      if (formattedMissionBills?.length > 0)
        await bulkEditMissionBill({
          data: { bills: formattedMissionBills },
        });
      if (newFormattedMissionBills?.length > 0)
        await createMissionBill({ data: newFormattedMissionBills });
      setIsOpen(true);
      await editMissionById({
        id: missionId,
        data: formatSubmittedData(data),
      });
      setIsOpen(true);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const onSubmit = async (data) => {
    const billingFields = getBillingRequiredFields({ isEdition: true });
    const hasBillingErrors = billingFields.some(({ field, check }) => {
      if (check(watch)) {
        setError(field, {
          type: "manual",
          message: "Ce champ est obligatoire",
        });
        return true;
      }
      return false;
    });

    if (hasBillingErrors) {
      setStatus(POPUP_STATUS.ERROR);
      setCode(FIELD_REQUIRED);
      setCellHighlight(true);
      changeSection(SECTIONS.BILLING);
      return;
    }

    // Warn only on an end date the user just set or changed, not on every save
    // of a mission that ended long ago.
    if (
      data.end_date &&
      !moment(data.end_date).isSame(missionBySlug?.end_date, "day") &&
      moment(data.end_date).isBefore(moment().startOf("day"))
    ) {
      pendingSubmitDataRef.current = data;
      setIsLateEndDateWarningOpen(true);
      return;
    }

    await submitMission(data);
  };

  const handleLateEndDateContinue = async () => {
    setIsLateEndDateWarningOpen(false);
    if (pendingSubmitDataRef.current) {
      await submitMission(pendingSubmitDataRef.current);
      pendingSubmitDataRef.current = null;
    }
  };

  const onError = (formErrors) => {
    handleSwitchDataValidation(watch, setError, switchDataNames);

    const hasSwitchErrors = switchDataNames.some(
      (switchName) => watch(switchName) === null
    );

    if (!_.isEmpty(formErrors) || hasSwitchErrors) {
      setStatus(POPUP_STATUS.ERROR);
      setCode(FIELD_REQUIRED);
      setCellHighlight(true);

      let targetSection;
      if (hasSwitchErrors) {
        targetSection = SECTIONS.GENERAL_INFO;
      } else {
        targetSection = determineSectionForErrors({
          errors: formErrors,
          watch,
          SECTIONS,
          dataTTV,
          isEdition: true,
        });
      }

      if (targetSection) {
        changeSection(targetSection);
      }
    }
  };

  const localNamesByIdHandler = (result) => {
    if (result?.length > 0) {
      setLocalNamesById((prev) => {
        const updated = { ...prev };
        result.forEach((item) => {
          updated[item.id] = item;
        });
        return updated;
      });
    }
  };

  useEffect(() => {
    if (!missionId) return;
    if (currentMissionIdRef.current !== missionId) {
      loadedUserIdsRef.current = new Set();
      currentMissionIdRef.current = missionId;
      setLocalNamesById({});
    }
    const allUserIds = [
      missionManagerId,
      missionClientId,
      missionSignedById,
      missionWorkerId,
      missionAdvId,
      missionRateCoordinator,
    ].filter(Boolean);

    const newUserIds = allUserIds.filter(
      (id) => !loadedUserIdsRef.current.has(id)
    );

    if (newUserIds.length > 0) {
      getNamesByFilters({
        types: [FILTERS_FOR_NAMES.PERSON, FILTERS_FOR_NAMES.CLIENT],
        id: newUserIds,
        include_deleted: true,
      }).then((result) => localNamesByIdHandler(result));
      newUserIds.forEach((id) => loadedUserIdsRef.current.add(id));
    }
    canUpdateMission &&
      getAvailableDocuments({ entityId: missionId, BASE_URL });
  }, [missionId, refresh]);

  const initForm = () => {
    const localNamesList = Object.values(localNamesById);
    const formattedData = formatDisplayReceivedData(missionBySlug, {
      usersByIds: personsList,
      branches,
      jobList,
      clientById,
      lang,
      namesByFilters: localNamesList,
    });
    Object.keys(formattedData).forEach((elm) => {
      setValue(elm, formattedData[elm]);
    });
  };

  useEffect(() => {
    fetchBranches();
    jobListGetter();
    appealListGetter();
    getDataTTV();
    currencyListGetter();
  }, [missionBySlug]);

  useEffect(() => {
    deleteMissionStatus !== QUERY_STATUS.INITIAL && setIsOpen(true);
  }, [deleteMissionStatus]);

  useEffect(() => {
    if (
      editMissionStatus !== QUERY_STATUS.INITIAL &&
      editMissionStatus !== QUERY_STATUS.LOADING
    ) {
      setIsOpen(true);
    }
  }, [editMissionStatus]);

  useEffect(() => {
    const requiredPersonIds = [
      missionManagerId,
      missionWorkerId,
      missionRateCoordinator,
      missionAdvId,
      missionClientId,
    ].filter(Boolean);

    const arePersonsLoaded = requiredPersonIds.every(
      (id) => localNamesById[id] !== undefined
    );

    const shouldInitialize =
      !_.isEmpty(missionBySlug) &&
      !_.isEmpty(jobList) &&
      arePersonsLoaded &&
      !isFormInitialized;

    if (shouldInitialize) {
      initForm();
      setIsFormInitialized(true);
    }
  }, [
    missionId,
    missionClientId,
    missionManagerId,
    missionWorkerId,
    missionRateCoordinator,
    missionAdvId,
    localNamesById,
    isFormInitialized,
    missionBySlug,
    jobList,
  ]);

  useEffect(() => {
    setIsFormInitialized(false);
  }, [missionId]);

  useEffect(() => {
    if (
      refresh &&
      missionFeeEditStatus === QUERY_STATUS.INITIAL &&
      missionManagerStatus === QUERY_STATUS.INITIAL
    ) {
      setIsFormInitialized(false);
    }
  }, [refresh, missionFeeEditStatus, missionManagerStatus]);

  const getOptions = (search, profile = "") => {
    getPersonsByProfile({ search, profile });
  };

  const onReset = () => {
    setCellHighlight(false);
    initForm();
    setReadOnly(true);
    clearErrors();
    setApiErrorResponse(null);
    setEditMissionStatus(QUERY_STATUS.INITIAL);
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    if (missionManagerId) {
      getAssignedBranchWithoutScope(missionManagerId);

      const currentBranch = branches?.find(
        (elm) => elm?.id === missionSignedById
      );

      setValue("signed_by_id", [
        {
          value: currentBranch?.id,
          type: TAG_TYPES.BRANCHES,
          label: currentBranch?.name,
        },
      ]);
    }
  }, [missionBySlug]);

  useEffect(() => {
    if (branches?.length > 0) {
      setManagerBranches(branches);
    }
  }, [branches]);

  const onCloseHandler = () => {
    setIsOpen(false);
    if (editMissionStatus === QUERY_STATUS.ERROR) return;
    setReadOnly(true);
    if (deleteMissionStatus === QUERY_STATUS.SUCCESS) {
      setMissionById({});
      setMissionBySlug({});
      setSlug("");
      navigate(`/${userScope}/missions`);
      getMissionsByFilters({
        page: 1,
        filters: getQueryParamsString({ filters: userFilterStorage }),
      });
    } else {
      changeSection(SECTIONS.GENERAL_INFO);
      navigate(`/${userScope}/missions/${missionSlug}`);
      setIsFormInitialized(false);
      setMissionById({});
      setMissionBySlug({});
      getMissionBySlug({ slug: missionSlug });
      setRefresh(true);
    }
    setDeleteMissionStatus(QUERY_STATUS.INITIAL);
    setEditMissionStatus(QUERY_STATUS.INITIAL);
    setApiErrorResponse(null);
  };

  const value = useMemo(
    () => ({
      entityStatus:
        missionFeeEditStatus !== QUERY_STATUS.INITIAL
          ? missionFeeEditStatus
          : missionManagerStatus,
      getClientById,
      clientById,
      errorDashDescriptionMessage,
      setErrorDashDescriptionMessage,
      descriptionMessageDashValue,
      setDescriptionMessageDashValue,
      clearErrors,
      missingCharValue,
      setMissingCharValue,
      errorNameMessage,
      setErrorNameMessage,
      errorWordDescriptionMessage,
      setErrorWordDescriptionMessage,
      descriptionMessageWordValue,
      setDescriptionMessageWordValue,
      setErrors,
      setError,
      cellHighlight,
      onDelete: deleteMissionById,
      mission_fee: missionBySlug?.current_fee,
      refresh,
      setRefresh,
      id: missionId,
      missionById: missionBySlug,
      setMissionCreated,
      userScope,
      tableBills,
      missionBillByMissionId,
      managerBranches,
      getStaffByArrayId,
      staffByIds,
      appealList,
      branches,
      clientsList: assignedClients,
      currencyList,
      currencyType,
      dataTTV,
      davFunctions: jobList,
      entity: ENTITY.MISSION,
      getClients: ({ search }) =>
        getAssignedClients({ search, clientTypes: [CLIENT_TYPES.BRANCH] }),
      getOptions,
      getPersonsByName: ({ search }) => searchUsersByName({ search }),
      isEnabled,
      lang,
      personsList,
      personsByProfileList,
      readOnly,
      searchUsers,
      section,
      setCurrencyType,
      setIsEnabled,
      setReadOnly,
      setSection,
      usersByIds,
      usersByName,
      defaultCurrency: _.chain(currencyList)
        .filter((elm) => elm?.id === missionCurrencyId)
        .first()
        .get("currency_symbol")
        .value(),
      getClientBillingAddressById,
      clientBillingAddressByIdStatus,
      setClientBillingAddressByIdStatus,
      clientBillingAddressById,
      setClientBillingAddressById,
      selectedMissionBillsTableDate,
      setSelectedMissionBillsTableDate,
      selectedMissionBillsTableYear,
      setSelectedMissionBillsTableYear,
      currentYear,
      getNamesByFilters,
      namesByFilters,
      historyManagersNames: namesByFilters,
      entitySlug: missionSlug,
      apiErrorResponse,
      editMissionStatus,
      changedActiveSection,
      availableDocuments,
      getDocument,
      getDocumentStatus,
      creationFileStatus,
      missionQuoteStatus,
      getMissionsByFilters,
      missionsByFilters,
      isMissionsPage,
      missionIsBackupable,
      openModalIntent,
      setOpenModalIntent,
    }),
    [
      getNamesByFilters,
      namesByFilters,
      missionIsBackupable,
      openModalIntent,
      getClientById,
      clientById,
      errorDashDescriptionMessage,
      setErrorDashDescriptionMessage,
      descriptionMessageDashValue,
      setDescriptionMessageDashValue,
      errors,
      clearErrors,
      missingCharValue,
      errorNameMessage,
      errorWordDescriptionMessage,
      descriptionMessageWordValue,
      cellHighlight,
      userScope,
      managerBranches,
      appealList,
      assignedClients,
      branches,
      currencyList,
      currencyType,
      dataTTV,
      isEnabled,
      jobList,
      lang,
      missionBySlug,
      personsList,
      personsByProfileList,
      readOnly,
      usersByIds,
      usersByName,
      staffByIds,
      missionBillByMissionId,
      tableBills,
      refresh,
      setRefresh,
      clientBillingAddressByIdStatus,
      clientBillingAddressById,
      setClientBillingAddressByIdStatus,
      selectedMissionBillsTableDate,
      selectedMissionBillsTableYear,
      currentYear,
      apiErrorResponse,
      editMissionStatus,
      missionFeeEditStatus,
      missionManagerStatus,
      changedActiveSection,
      availableDocuments,
      getDocument,
      isMissionsPage,
    ]
  );

  if (!isFormInitialized) {
    return <LoaderV2 loaderSize="fullpage" />;
  }

  return (
    <InfoContext.Provider value={value}>
      <InfoHeaderAction />
      <DocumentDownloadModal
        status={downloadStatus}
        onClose={onDownloadModalClose}
      />
      <PastDateWarningModal
        open={isLateEndDateWarningOpen}
        onContinue={handleLateEndDateContinue}
        onClose={() => setIsLateEndDateWarningOpen(false)}
        messageId="modal_late_end_date_warning_msg"
      />
      <ValidationModal
        messages={{
          pendingTitleId: `${
            deleteMissionStatus === QUERY_STATUS.LOADING
              ? "modal_validation_delete_mission_action_msg"
              : "modal_validation_edit_file"
          }`,
          pendingMessageId: `${
            deleteMissionStatus === QUERY_STATUS.LOADING
              ? "modal_validation_delete_mission_pending_msg"
              : "modal_validation_file_pending_msg"
          }`,
          successTitleId: `${
            deleteMissionStatus === QUERY_STATUS.SUCCESS
              ? "modal_validation_delete_mission_action_msg"
              : "modal_validation_edit_file"
          }`,
          successMessageId: `${
            deleteMissionStatus === QUERY_STATUS.SUCCESS
              ? "modal_validation_delete_mission_success_msg"
              : "modal_validation_file_success_msg"
          }`,
          customMessages: Array.isArray(apiErrorResponse)
            ? apiErrorResponse
            : null,
          errorMessageId: "modal_validation_field_errors_msg",
          errorMessageDefault:
            "Certains champs comportent des erreurs.{br}Veuillez cliquer sur les icônes :  <nowrap>{errorIcon}</nowrap> pour obtenir plus de détails et corriger les informations nécessaires.",
        }}
        open={isOpen}
        status={
          deleteMissionStatus !== QUERY_STATUS.INITIAL
            ? deleteMissionStatus
            : editMissionStatus
        }
        onClose={() => onCloseHandler()}
        isCustomModale={
          editMissionStatus !== QUERY_STATUS.SUCCESS &&
          deleteMissionStatus !== QUERY_STATUS.SUCCESS &&
          Array.isArray(apiErrorResponse)
        }
        customTitle={
          deleteMissionStatus !== QUERY_STATUS.INITIAL
            ? {
                id: "modal_delete_mission_title",
                defaultMessage: "Suppression de la mission",
              }
            : {
                id: "modal_edit_mission_title",
                defaultMessage: "Edition de la mission",
              }
        }
      />
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        {section === SECTIONS.GENERAL_INFO && (
          <MissionGeneralInfo
            clearErrors={clearErrors}
            code={code}
            control={control}
            errors={errors}
            getCode={setCode}
            getStatus={setStatus}
            getValues={getValues}
            register={register}
            setErrors={setErrors}
            setValue={setValue}
            unregister={unregister}
            watch={watch}
          />
        )}
        {section === SECTIONS.BILLING && (
          <MissionBillingInfo
            clearErrors={clearErrors}
            code={code}
            control={control}
            errors={errors}
            getCode={setCode}
            getStatus={setStatus}
            readOnly={readOnly}
            register={register}
            setValue={setValue}
            getValues={getValues}
            unregister={unregister}
            watch={watch}
            mission_fee={missionBySlug?.current_fee}
          />
        )}
        {!readOnly && (
          <div style={{ marginTop: "50px" }} className="form-buttons">
            <Button
              className="btn-standard btn-outline"
              type="button"
              onClick={onReset}
              id="button-form-reset"
            >
              <FormattedMessage id="cancel" defaultMessage="Annuler" />
            </Button>
            <Button
              color="btn-medium gray-700"
              type="submit"
              id="button-form-validation"
              onClick={() => setIsValidated(true)}
              backgroundColor={
                (editMissionStatus === QUERY_STATUS.LOADING ||
                  bulkEditMissionBillStatus === QUERY_STATUS.LOADING) &&
                colors.disabledGrey
              }
              disabled={
                editMissionStatus === QUERY_STATUS.LOADING ||
                bulkEditMissionBillStatus === QUERY_STATUS.LOADING
              }
            >
              <FormattedMessage
                id="button_form_validation_edition"
                defaultMessage="Valider l'édition"
              />
            </Button>
          </div>
        )}
      </form>
    </InfoContext.Provider>
  );
};

export default DisplayMission;

DisplayMission.propTypes = {
  code: propTypes.string,
  setCode: propTypes.func,
  setStatus: propTypes.func,
};
